import { useState } from "react";
import { AGE_RANGES, COMPLETING_FOR } from "../../lib/options";
import { buildPayload, useSubmit, validateContact, validateLocation } from "../../lib/forms";
import { Wizard } from "./Wizard";
import { ChoiceCards, ConsentBlock, ContactFields, Field, Group, Honeypot, LocationFields, MultiChips, NativeSelect, SuccessPanel, TextArea, TextField } from "./FormBits";
import { Notice } from "../site/Blocks";

const SUPPORTS = ["Companionship", "Personal support (bathing, dressing, grooming)", "Help with activities of daily living",
  "Meal preparation", "Mobility assistance", "Light housekeeping", "Medication reminders", "Errands and accompaniment to appointments",
  "Respite for family caregivers", "Other non-medical support"];
const FREQUENCY = ["One-time or short-term", "A few hours per week", "Several days per week", "Daily", "Overnight support", "Live-in support", "Not sure yet"];
const TIMES = ["Mornings", "Afternoons", "Evenings", "Overnight", "Weekends"];
const TIMING = ["As soon as possible", "Within the next month", "In 1–3 months", "Planning ahead", "On a specific date"];
const CONSIDERATIONS = ["Memory changes or dementia", "Recently home from hospital", "Fall risk or balance concerns", "Lives alone",
  "Pets in the home", "Cultural or dietary preferences", "Preference for caregiver gender", "Smoke-free environment preferred"];
const GOV = "Government or agency-funded program";
const PAYMENT = ["Private pay", "Private or long-term care insurance", GOV,
  "Veterans or employer benefits", "Not sure yet", "Other"];

const NO_HOURS = ["One-time or short-term", "Live-in support", "Not sure yet"];

const clean = (f) => {
  const c = { ...f };
  if (f.completing_for === "self") Object.assign(c, { relationship: "", organization: "", recipient_first_name: "", person_aware: "" });
  if (f.completing_for === "professional") c.relationship = "";
  if (["family", "friend"].includes(f.completing_for)) c.organization = "";
  if (!f.supports.includes("Mobility assistance")) c.mobility_aids = "";
  if (!f.supports.includes("Respite for family caregivers")) c.respite_duration = "";
  if (!f.supports.includes("Other non-medical support")) c.supports_other = "";
  if (NO_HOURS.includes(f.frequency)) c.hours_per_week = "";
  if (f.start_timing !== "On a specific date") c.start_date = "";
  if (f.payment_method !== "Other") c.payment_other = "";
  if (f.payment_method !== GOV) c.funding_details = "";
  return c;
};

const INITIAL = {
  completing_for: "", relationship: "", organization: "", recipient_first_name: "", person_aware: "", age_range: "",
  region: "", city: "", city_custom: "", supports: [], supports_other: "", mobility_aids: "", respite_duration: "",
  frequency: "", hours_per_week: "", preferred_times: [], start_timing: "", start_date: "", considerations: [],
  language_preference: "", additional_notes: "", payment_method: "", payment_other: "", funding_details: "", name: "", email: "", phone: "",
  preferred_contact: "", privacy_consent: false, marketing_consent: false, website: "",
};

export const CareQuestionnaire = () => {
  const [form, setForm] = useState(INITIAL);
  const { submit, submitting, error, reference, reset } = useSubmit();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const forOther = form.completing_for && form.completing_for !== "self";
  const needsHours = form.frequency && !NO_HOURS.includes(form.frequency);

  if (reference) {
    return (
      <SuccessPanel reference={reference} title="Thank you. Your care inquiry has been received." testId="care-success"
        onReset={() => { setForm(INITIAL); reset(); }}>
        <p>A member of our team will review your inquiry and contact you to discuss your needs and the options that may be available.</p>
        <p>Submitting an inquiry does not confirm caregiver availability, a start date, or eligibility for insurance or funding. These are discussed and confirmed with you directly.</p>
      </SuccessPanel>
    );
  }

  const steps = [
    {
      title: "About this inquiry", short: "About",
      validate: () => (!form.completing_for ? "Please tell us who you are completing this inquiry for." : !form.age_range ? "Please select an age range." : null),
      content: (
        <>
          <Group legend="Who is this inquiry for?" required>
            <ChoiceCards options={COMPLETING_FOR} value={form.completing_for} onChange={(v) => set("completing_for", v)} testId="care-completing-for" />
          </Group>
          {forOther && (
            <div className="grid sm:grid-cols-2 gap-5 animate-step">
              {form.completing_for === "professional" ? (
                <TextField id="care-org" label="Organization (optional)" value={form.organization} onChange={(v) => set("organization", v)} testId="care-input-organization" />
              ) : (
                <TextField id="care-relationship" label="Your relationship to the person" value={form.relationship} onChange={(v) => set("relationship", v)} placeholder="e.g. daughter, neighbour" testId="care-input-relationship" />
              )}
              <TextField id="care-recipient" label="Their first name (optional)" value={form.recipient_first_name} onChange={(v) => set("recipient_first_name", v)} testId="care-input-recipient-name" />
              <Field label="Is the person aware of this inquiry?" htmlFor="care-aware">
                <NativeSelect id="care-aware" value={form.person_aware} onChange={(v) => set("person_aware", v)} testId="care-input-person-aware"
                  options={["Yes, they are aware and agree", "Not yet", "They need support with decision-making", "Prefer to discuss"]} />
              </Field>
            </div>
          )}
          <Group legend="Age range of the person needing care" required>
            <ChoiceCards options={AGE_RANGES} value={form.age_range} onChange={(v) => set("age_range", v)} cols="grid-cols-2 sm:grid-cols-5" testId="care-age" />
          </Group>
        </>
      ),
    },
    {
      title: "Location", short: "Location", validate: () => validateLocation(form),
      content: <LocationFields form={form} set={set} prefix="care" />,
    },
    {
      title: "Support needed", short: "Support", description: "Select all that apply. We provide non-medical support only.",
      validate: () => (form.supports.length === 0 ? "Please select at least one type of support." : null),
      content: (
        <>
          <Group legend="What kind of support are you looking for?" required>
            <MultiChips options={SUPPORTS} value={form.supports} onChange={(v) => set("supports", v)} testId="care-support" />
          </Group>
          {form.supports.includes("Mobility assistance") && (
            <Field label="Which mobility aids, if any, are used?" htmlFor="care-mobility">
              <NativeSelect id="care-mobility" value={form.mobility_aids} onChange={(v) => set("mobility_aids", v)} testId="care-input-mobility-aids"
                options={["None", "Cane", "Walker or rollator", "Wheelchair", "Needs help with transfers", "Not sure"]} />
            </Field>
          )}
          {form.supports.includes("Respite for family caregivers") && (
            <Field label="What kind of respite would help?" htmlFor="care-respite">
              <NativeSelect id="care-respite" value={form.respite_duration} onChange={(v) => set("respite_duration", v)} testId="care-input-respite"
                options={["A few hours at a time", "A full day", "Several days", "Regular weekly breaks", "Not sure yet"]} />
            </Field>
          )}
          {form.supports.includes("Other non-medical support") && (
            <TextField id="care-support-other" label="Please describe the other support" value={form.supports_other} onChange={(v) => set("supports_other", v)} maxLength={400} testId="care-input-support-other" />
          )}
        </>
      ),
    },
    {
      title: "Schedule and timing", short: "Schedule",
      validate: () => (!form.frequency ? "Please select how often support is needed." : !form.start_timing ? "Please tell us your preferred start timing."
        : form.start_timing === "On a specific date" && !form.start_date ? "Please choose a preferred start date." : null),
      content: (
        <>
          <Group legend="How often is support needed?" required>
            <ChoiceCards options={FREQUENCY} value={form.frequency} onChange={(v) => set("frequency", v)} cols="sm:grid-cols-2 lg:grid-cols-3" testId="care-frequency" />
          </Group>
          {needsHours && (
            <Field label="Approximate hours per week" htmlFor="care-hours">
              <NativeSelect id="care-hours" value={form.hours_per_week} onChange={(v) => set("hours_per_week", v)} testId="care-input-hours"
                options={["Under 5 hours", "5–10 hours", "10–20 hours", "20–40 hours", "More than 40 hours", "Not sure"]} />
            </Field>
          )}
          <Group legend="Preferred times (optional)">
            <MultiChips options={TIMES} value={form.preferred_times} onChange={(v) => set("preferred_times", v)} testId="care-times" />
          </Group>
          <Group legend="When would you like support to begin?" required>
            <ChoiceCards options={TIMING} value={form.start_timing} onChange={(v) => set("start_timing", v)} cols="sm:grid-cols-2 lg:grid-cols-3" testId="care-start" />
          </Group>
          {form.start_timing === "On a specific date" && (
            <TextField id="care-date" label="Preferred start date" type="date" value={form.start_date} onChange={(v) => set("start_date", v)} required
              min={new Date().toISOString().slice(0, 10)} testId="care-input-start-date" />
          )}
          <Notice>Start dates depend on caregiver availability in your area and are confirmed with you directly.</Notice>
        </>
      ),
    },
    {
      title: "Care considerations and coverage", short: "Details",
      validate: () => (!form.payment_method ? "Please select the anticipated funding or coverage."
        : form.payment_method === "Other" && !form.payment_other.trim() ? "Please describe the other funding or coverage."
        : form.payment_method === GOV && !form.funding_details.trim() ? "Please tell us which program or agency provides the funding." : null),
      content: (
        <>
          <Group legend="Is there anything we should be aware of? (optional)" hint="General considerations only. Please do not share detailed medical history.">
            <MultiChips options={CONSIDERATIONS} value={form.considerations} onChange={(v) => set("considerations", v)} testId="care-consider" />
          </Group>
          <TextField id="care-language" label="Preferred language (optional)" value={form.language_preference} onChange={(v) => set("language_preference", v)} testId="care-input-language" />
          <TextArea id="care-notes" label="Anything else you would like us to know? (optional)" value={form.additional_notes} onChange={(v) => set("additional_notes", v)}
            hint="Please avoid detailed medical information, health card numbers or financial details." max={1000} testId="care-input-notes" />
          <Group legend="Anticipated funding or coverage" required hint="How do you expect care to be funded?">
            <ChoiceCards options={PAYMENT} value={form.payment_method} onChange={(v) => set("payment_method", v)} testId="care-payment" />
          </Group>
          {form.payment_method === GOV && (
            <TextField id="care-funding-details" label="Which program or agency provides the funding?" value={form.funding_details} onChange={(v) => set("funding_details", v)} required maxLength={400}
              placeholder="e.g. Ontario Health atHome, a case manager's agency" hint="Program name and, if known, your case manager's organization." testId="care-input-funding-details" />
          )}
          {form.payment_method === "Other" && (
            <TextField id="care-payment-other" label="Please describe" value={form.payment_other} onChange={(v) => set("payment_other", v)} required maxLength={400} testId="care-input-payment-other" />
          )}
          <Notice>We cannot guarantee insurance reimbursement or eligibility for government or agency funding. Payment arrangements are discussed with you directly.</Notice>
        </>
      ),
    },
    {
      title: "Your contact details", short: "Contact", validate: () => validateContact(form),
      content: (
        <>
          <ContactFields form={form} set={set} prefix="care" />
          <ConsentBlock form={form} set={set} prefix="care" purpose="care inquiry" />
          <Honeypot value={form.website} onChange={(v) => set("website", v)} />
        </>
      ),
    },
  ];

  return (
    <Wizard steps={steps} testPrefix="care" submitting={submitting} error={error} submitLabel="Submit care inquiry"
      onSubmit={() => submit("/public/care-inquiry", buildPayload(clean(form)))} />
  );
};
